import {
  Checkbox,
  FormControlLabel,
  FormHelperTextProps,
  IconButton,
  InputAdornment,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  ProjectStateDto,
  ProjectSummaryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { makeStyles } from 'tss-react/mui'
import SearchTextField, {
  SearchTextFieldRef,
} from '../../Common/SearchTextBox/SearchTextField'
import KanbanProjectSummaryCard from './KanbanProjectSummaryCard'

import { ClearOutlined, SearchOutlined } from '@mui/icons-material'
import { RefreshButton } from 'components/Common/Button/RefreshWithProgress'
import DateRangeFilter from 'components/Common/DateRangeFilter'
import { useClientStorage } from 'customHooks/useClientStorage'
import moment from 'moment'
import React from 'react'
import * as ReactTrelloModule from 'react-trello'
import { useAppSelector } from 'store/configureStore'
import { resolveDefaultExport } from 'utils/resolveDefaultExport'
import ScrollableLane from './KanbanComponents/ScrollableLane'
import { dispatchKanbanEvents } from './kanbanEvents'
import './KanbanView.sass'
import { useProjectsKanban } from './useProjectKanban'

const Board = resolveDefaultExport(ReactTrelloModule)

const threeMonthsAgo = moment().subtract(3, 'months')

const useStyles = makeStyles()((theme: Theme) => ({
  board: {
    width: '100%',
    margin: '0 auto',
    color: theme.palette.text.primary,
    '& .react-trello-lane': {
      // height: 'calc(100vh - 240px)',
      height: '100%',
      maxHeight: '100%',
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderRight: `3px dashed ${theme.palette.divider}`,
      justifyContent: 'space-between',
      // borderRadius: theme.shape.borderRadius,
    },
  },
  toolbar: {
    padding: theme.spacing(2, 8),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
  search: {
    [theme.breakpoints.up('lg')]: {
      width: '24vw',
    },
  },
}))

export const ProjectsKanban = (props: {
  isFirstRender: boolean
  currentQuery: string
  onQueryChange: (query: string) => void
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()

  const isBuyerPartyView = useAppSelector(
    (state) => state.user.organizationContext.isBuyingParty,
  )

  const {
    onlyMyProjects,
    setOnlyMyProjects,
    updateProjectStatus,
    handleSetPriority,
    setShowCanceledProjects,
    showCanceledProjects,
    getProjectListAndLanes,
    lanes,
    setLanes,
    boardRef,
    kanbanProjectStates,
    eventBus,
    setEventBus,
    resetPagination,
    setOnlyCreatedInPortal,
    onlyCreatedInPortal,
    fromDate,
    toDate,
    handleSetFromDate,
    handleSetToDate,
    partyDefaultDateFormat,
  } = useProjectsKanban()

  const updateKanbanBoard = useCallback(
    async (
      projectStates: undefined | Partial<ProjectStateDto>[],
      onlyMyProjects: boolean,
      onlyCreatedInPortal: boolean,
      showCanceledProjects: boolean,
      fromDate: Date,
      toDate: Date,
    ) => {
      try {
        const { lanes: updatedLanes } = await getProjectListAndLanes(
          projectStates ?? kanbanProjectStates,
          onlyMyProjects,
          onlyCreatedInPortal,
          showCanceledProjects,
          fromDate,
          toDate,
        )

        const allLanes = lanes.lanes.map((lane) => {
          const updatedLane = updatedLanes.lanes.find((x) => x.id === lane.id)
          if (updatedLane) {
            return updatedLane
          }
          return lane
        })

        setLanes({ lanes: allLanes })

        eventBus.current?.publish({
          type: 'REFRESH_BOARD',
          data: { lanes: allLanes },
        })
      } catch (err) {
        ShowException('unable to refresh', err)
      }
    },
    [
      getProjectListAndLanes,
      kanbanProjectStates,
      lanes?.lanes,
      setLanes,
      eventBus,
    ],
  )

  const [currentQuery, setCurrentQuery] = useClientStorage('currentQuery', '')

  const { t } = useTranslation()
  const searchInputRef = React.createRef<SearchTextFieldRef>()

  const handleDragEnd = async (
    cardId: string,
    sourceLaneId: string,
    targetLaneId: string,
    position: number,
    cardDetails: { id: string; laneId: string; metadata: ProjectSummaryDto },
  ) => {
    let sourceLane = sourceLaneId ?? cardDetails?.metadata.status
    sourceLane = sourceLane[0].toUpperCase() + sourceLane.slice(1)

    try {
      if (!sourceLane) {
        console.info('[handleDragEnd]: no source lane id or card lane id')
        return false
      }

      eventBus.current?.publish({
        type: 'MOVE_CARD',
        fromLaneId: sourceLane,
        toLaneId: targetLaneId,
        cardId: cardId,
        index: 0,
      })

      await updateProjectStatus(
        cardDetails.id,
        sourceLane,
        targetLaneId,
        0, // position
      )

      // returning false will make the react-trello library do not update the
      // board state (we did it manually)
      return false
    } catch (err) {
      ShowException('update project', err)
      eventBus.current?.publish({
        type: 'MOVE_CARD',
        fromLaneId: targetLaneId,
        toLaneId: sourceLane,
        cardId: cardId,
        index: position,
      })

      return false
    }
  }

  const onSetPriority = useCallback(
    async (laneId: ProjectStateDto, projectId: string, priority: number) => {
      try {
        await handleSetPriority(laneId, projectId, priority)

        // await updateKanbanBoard([laneId], onlyMyProjects, showCanceledProjects)
      } catch (err) {
        ShowException('set priority', err)
        updateKanbanBoard(
          undefined,
          onlyMyProjects,
          onlyCreatedInPortal,
          showCanceledProjects,
          fromDate,
          toDate,
        )
      }
    },
    [
      fromDate,
      handleSetPriority,
      onlyCreatedInPortal,
      onlyMyProjects,
      showCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const handleCardClick = useCallback(
    (cardId: string) => {
      navigate(`/app/project/${cardId}`)
    },
    [navigate],
  )

  const handleSearch = useCallback(
    async (search: string) => {
      setCurrentQuery(search)

      resetPagination()

      await updateKanbanBoard(
        undefined,
        onlyMyProjects,
        onlyCreatedInPortal,
        showCanceledProjects,
        fromDate,
        toDate,
      )

      props.onQueryChange(search)
    },
    [
      fromDate,
      onlyCreatedInPortal,
      onlyMyProjects,
      props,
      resetPagination,
      setCurrentQuery,
      showCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const handleReload = useCallback(() => {
    // dispatchKanbanEvents('REFRESH_ALL')

    resetPagination()

    setTimeout(async () => {
      await updateKanbanBoard(
        kanbanProjectStates,
        onlyMyProjects,
        onlyCreatedInPortal,
        showCanceledProjects,
        fromDate,
        toDate,
      )
    }, 0)
  }, [
    resetPagination,
    updateKanbanBoard,
    kanbanProjectStates,
    onlyMyProjects,
    onlyCreatedInPortal,
    showCanceledProjects,
    fromDate,
    toDate,
  ])

  const handleOnlyCreatedInPortal = useCallback(
    async (_, createdInPortal: boolean) => {
      setOnlyCreatedInPortal(createdInPortal)

      await updateKanbanBoard(
        undefined,
        onlyMyProjects,
        createdInPortal,
        showCanceledProjects,
        fromDate,
        toDate,
      )
    },
    [
      fromDate,
      onlyMyProjects,
      setOnlyCreatedInPortal,
      showCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const handleOnlyMyProjects = useCallback(
    async (_, filterMyProjects: boolean) => {
      setOnlyMyProjects(filterMyProjects)
      await updateKanbanBoard(
        undefined,
        filterMyProjects,
        onlyCreatedInPortal,
        showCanceledProjects,
        fromDate,
        toDate,
      )
    },
    [
      fromDate,
      onlyCreatedInPortal,
      setOnlyMyProjects,
      showCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const KanbanCard = useCallback(
    (cardProps) => {
      return (
        <KanbanProjectSummaryCard
          {...cardProps}
          // loading={loading}
          setPriority={onSetPriority}
        />
      )
    },
    [onSetPriority],
  )

  const handleIncludeCanceledProjects = useCallback(
    async (e, checked) => {
      setShowCanceledProjects(checked)
      if (checked) {
        await updateKanbanBoard(
          [ProjectStateDto.Cancelled],
          onlyMyProjects,
          onlyCreatedInPortal,
          checked,
          fromDate,
          toDate,
        )

        dispatchKanbanEvents('FETCH_NEXT_PAGE_ENDED', {
          error: undefined,
          isLastPage: false,
          lastPageFetched: 0,
          projectState: ProjectStateDto.Cancelled,
          success: true,
        })
      }
    },
    [
      fromDate,
      onlyCreatedInPortal,
      onlyMyProjects,
      setShowCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const paginateLane = useCallback(
    async (requestedPage: number, laneId: string) => {
      try {
        const { lanes } = await getProjectListAndLanes(
          [laneId as never],
          onlyMyProjects,
          onlyCreatedInPortal,
          showCanceledProjects,
          fromDate,
          toDate,
          requestedPage,
        )

        return lanes.lanes
          .filter((x) => x.id === laneId)
          .flatMap((x) => x.cards)
      } catch (err) {
        ShowException('pagination', err)
        return null
      }
    },
    [
      fromDate,
      getProjectListAndLanes,
      onlyCreatedInPortal,
      onlyMyProjects,
      showCanceledProjects,
      toDate,
    ],
  )
  const onChangeFromDate = useCallback(
    async (from: Date) => {
      handleSetFromDate(from)
      await updateKanbanBoard(
        undefined,
        onlyMyProjects,
        onlyCreatedInPortal,
        showCanceledProjects,
        from,
        toDate,
      )
    },
    [
      handleSetFromDate,
      onlyCreatedInPortal,
      onlyMyProjects,
      showCanceledProjects,
      toDate,
      updateKanbanBoard,
    ],
  )

  const onChangeToDate = useCallback(
    async (to: Date) => {
      handleSetToDate(to)
      await updateKanbanBoard(
        undefined,
        onlyMyProjects,
        onlyCreatedInPortal,
        showCanceledProjects,
        fromDate,
        to,
      )
    },
    [
      fromDate,
      handleSetToDate,
      onlyCreatedInPortal,
      onlyMyProjects,
      showCanceledProjects,
      updateKanbanBoard,
    ],
  )

  return (
    <Paper className={classes.board}>
      <div className={classes.toolbar}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'baseline',
            gap: '.5em',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              gap: '.5em',
            }}
          >
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={onChangeFromDate}
              onToDateChange={onChangeToDate}
              variant="standard"
              size="small"
              fromDateLabel={t('common:from-date', 'from date')}
              toDateLabel={t('common:to-date', 'to date')}
              fromDatePlaceholder={t('common:created-on', 'created on')}
              toDatePlaceholder={t('common:created-on', 'created on')}
              showAndText={false}
            />
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                  gap: '.5em',
                  flexDirection: 'column',
                }}
              >
                <div style={{ display: 'flex', gap: '2em' }}>
                  <Tooltip
                    title={t(
                      'common:my-projects-filter-help',
                      'shows only projects created by or where the sales representative is assigned to my user',
                    )}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          id={'only-my-projects'}
                          checked={onlyMyProjects}
                          onChange={handleOnlyMyProjects}
                          style={{
                            padding: 0,
                            marginRight: '.5em',
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {t('common:my-projects', 'my projects')}
                        </Typography>
                      }
                    />
                  </Tooltip>
                  {!isBuyerPartyView && (
                    <Tooltip
                      title={t(
                        'common:created-in-portal-tooltip',
                        'shows only projects created in the portal',
                      )}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            id={'only-created-in-portal'}
                            checked={onlyCreatedInPortal}
                            onChange={handleOnlyCreatedInPortal}
                            style={{
                              padding: 0,
                              marginRight: '.5em',
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {t(
                              'common:created-in-portal-filter',
                              'self-service projects only',
                            )}
                          </Typography>
                        }
                      />
                    </Tooltip>
                  )}
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      id={'show-canceled-projects'}
                      checked={showCanceledProjects}
                      onChange={handleIncludeCanceledProjects}
                      style={{
                        padding: 0,
                        marginRight: '.5em',
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {t(
                        'common:show-canceled-projects-label',
                        'include canceled and expired projects',
                      )}
                    </Typography>
                  }
                />
              </div>
            </div>
            <div>
              <SearchTextField
                // searchOnEnterOrBlur
                onSearch={handleSearch}
                defaultValue={currentQuery}
                slotProps={{
                  input: {
                    id: 'search-projects',
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            const searchInput = document.getElementById(
                              'search-projects',
                            ) as HTMLInputElement

                            if (searchInput) {
                              searchInputRef.current.clear()
                              searchInput.value = ''
                            }
                            handleSearch('')
                          }}
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.action.disabled,
                          }}
                        >
                          <ClearOutlined />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                  formHelperText: {
                    component: 'div',
                  },
                }}
                slots={{
                  formHelperText: (
                    formHelperTextProps: FormHelperTextProps,
                  ) => {
                    return <>{formHelperTextProps.children}</>
                  },
                }}
                className={classes.search}
                ref={searchInputRef}
                variant="outlined"
                size="small"
                style={{
                  minWidth: '250px',
                }}
              />
              <RefreshButton
                // loading={loading['GetProjectsListPaged']}
                loading={false}
                onClick={handleReload}
                size="small"
              />
            </div>
          </div>
        </div>
      </div>
      <div style={{ width: '100%', overflow: 'auto' }}>
        <LoadingContainer loading={!lanes} noMessage>
          <div style={{ width: '100%' }}>
            {lanes?.lanes?.length && (
              <Board
                ref={boardRef}
                data={lanes}
                components={{

                  Card: KanbanCard,
                  ScrollableLane: ScrollableLane,
                }}
                onLaneScroll={paginateLane}
                hideCardDeleteIcon
                handleDragEnd={handleDragEnd}
                onCardClick={handleCardClick}
                eventBusHandle={setEventBus}
              />
            )}
          </div>
        </LoadingContainer>
      </div>
    </Paper>
  )
}
